import React, { useState, useContext } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithPopup
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft, X } from 'lucide-react';
import BenyImg from '../assets/beny-3d.png';
import IatCursorGlow from './IatCursorGlow';
import { CompanyContext } from '../contexts/CompanyContext';
import TrialExpiredModal from './TrialExpiredModal';
import axios from '../config/axios';
import { logger } from '../utils/logger';

// ─── Brand tokens (inline pra preservar fidelidade da tela) ───
const IAT = {
  yellow:     '#FFC107',
  yellowDeep: '#F59E0B',
  graphite:   '#1A1A20',
  charcoal:   '#232329',
  slate:      '#2D2D35',
  iron:       '#5A5A65',
  silver:     '#9A9AA5',
  pewter:     '#C4C4CC',
  cloud:      '#E8E8EC',
  paper:      '#FAFAFB',
  white:      '#FFFFFF',
  ink:        '#0E0E12',
  borderDark: '#2D2D35',
};

const FONT_SANS    = "'Inter', -apple-system, system-ui, sans-serif";
const FONT_DISPLAY = "'Inter Tight', 'Inter', system-ui, sans-serif";
const FONT_SERIF   = "'Instrument Serif', 'Times New Roman', serif";

const GoogleLogo = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const CompanySelectionModal = ({ companies, onSelect, onClose }) => {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: 'rgba(11,11,18,0.72)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-xl p-6 relative"
        style={{
          background: IAT.charcoal,
          border: `1px solid ${IAT.borderDark}`,
          color: IAT.paper,
          fontFamily: FONT_SANS,
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 transition-colors"
          style={{ color: IAT.silver }}
          aria-label="Fechar"
        >
          <X size={20} />
        </button>
        <h2
          className="text-xl mb-6 text-center"
          style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.02em' }}
        >
          Selecione uma empresa
        </h2>
        <ul className="space-y-3">
          {companies.map((company) => (
            <li
              key={company.companyId}
              onClick={() => onSelect(company)}
              className="cursor-pointer p-4 rounded-xl transition-all"
              style={{
                border: `1px solid ${IAT.slate}`,
                background: IAT.graphite,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = IAT.yellow;
                e.currentTarget.style.background = IAT.slate;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = IAT.slate;
                e.currentTarget.style.background = IAT.graphite;
              }}
            >
              <span style={{ fontSize: 15, color: IAT.paper }}>
                {company.companyName || 'Empresa sem nome'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Caption = ({ children, style }) => (
  <div
    style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: IAT.iron, ...style,
    }}
  >
    {children}
  </div>
);

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [trialExpiredData] = useState(null);
  const auth = getAuth();
  const navigate = useNavigate();
  const { updateSelectedCompany } = useContext(CompanyContext);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || axios.defaults.baseURL || '';

  const handleBackToPlans = () => {
    window.location.assign('https://www.iatconsulting.com.br/planos');
  };

  const handlePostLogin = async (user) => {
    try {
      logger.log('[LoginScreen] Iniciando handlePostLogin para usuário:', user.uid);
      const idToken = await user.getIdToken();
      const response = await fetch(`${API_BASE}/api/user-companies`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      });
      if (!response.ok) {
        let errorData;
        try {
          const text = await response.text();
          errorData = text ? JSON.parse(text) : { error: 'Erro desconhecido' };
        } catch (e) {
          errorData = { error: `Erro ${response.status}: ${response.statusText}` };
        }
        if (response.status === 403 && errorData.error === 'blocked_user') {
          setError(errorData.message || 'Sua conta está bloqueada em todas as empresas. Entre em contato com o administrador.');
          return;
        }
        throw new Error(errorData.error || 'Erro ao buscar empresas do usuário');
      }
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new Error('Resposta inválida do servidor');
      }
      const userCompanies = data.companies || [];
      if (userCompanies.length === 0) {
        setError('Nenhuma empresa associada ao usuário.');
        return;
      }
      if (userCompanies.length === 1) {
        const company = userCompanies[0];
        if (!company || !company.companyId) {
          setError('Dados da empresa inválidos.');
          return;
        }
        try {
          const updateResponse = await fetch(`${API_BASE}/update-last-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({ companyId: company.companyId })
          });
          if (!updateResponse.ok) {
            const errorData = await updateResponse.json();
            if (updateResponse.status === 403 && errorData.error === 'Usuário bloqueado nesta empresa') {
              setError('Sua conta está bloqueada nesta empresa. Entre em contato com o administrador.');
              return;
            }
          }
        } catch (error) {
          console.error('Erro ao atualizar último login:', error);
        }
        updateSelectedCompany(company);
        const userRole = (company.role || company.user?.role || 'atendente').toLowerCase();
        if (userRole === 'admin') {
          navigate(`/settings?companyId=${company.companyId}`, { replace: true });
        } else {
          navigate(`/chat?companyId=${company.companyId}`, { replace: true });
        }
      } else {
        const sortedCompanies = userCompanies.sort((a, b) =>
          (a.companyName || '').localeCompare(b.companyName || '')
        );
        setCompanies(sortedCompanies);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Erro detalhado no handlePostLogin:', error);
      setError('Ocorreu um erro ao processar o login. Por favor, tente novamente.');
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await user.getIdToken(true);
      await handlePostLogin(user);
    } catch (error) {
      console.error('Erro detalhado no login:', error);
      if (error.code === 'auth/wrong-password') setError('Senha incorreta. Tente novamente.');
      else if (error.code === 'auth/user-not-found') setError('Usuário não encontrado. Por favor, cadastre-se.');
      else if (error.code === 'auth/too-many-requests') setError('Muitas tentativas de login. Tente novamente mais tarde.');
      else if (error.message?.includes('CPF ou CNPJ inválido')) setError('CPF ou CNPJ inválido. Por favor, verifique e tente novamente.');
      else if (error.message?.includes('Número de WhatsApp inválido')) setError('Número de WhatsApp inválido. Por favor, verifique e tente novamente.');
      else if (error.message?.includes('Conta está desativada')) setError('Sua conta está desativada. Por favor, contate o suporte.');
      else if (error.message?.includes('Usuário está banido')) setError('Sua conta foi banida. Por favor, contate o suporte.');
      else setError('Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account', login_hint: email });
    try {
      const result = await signInWithPopup(auth, provider);
      await handlePostLogin(result.user);
    } catch (error) {
      console.error('Erro ao fazer login com Google:', error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        const existingEmail = error.customData?.email;
        if (password && existingEmail) {
          try {
            const userCredential = await signInWithEmailAndPassword(auth, existingEmail, password);
            await linkWithPopup(userCredential.user, provider);
            await userCredential.user.reload();
            await handlePostLogin(userCredential.user);
          } catch (linkError) {
            setError('Erro ao vincular credenciais: ' + linkError.message);
          }
        } else {
          setError('Conta já existente. Por favor, faça login com email e senha para vincular sua conta do Google.');
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('Login cancelado pelo usuário.');
      } else {
        setError('Erro ao fazer login com Google. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Por favor, insira seu endereço de e-mail.');
      return;
    }
    try {
      // E-mail branded da IAT (em vez do e-mail/tela genéricos do Firebase).
      // O backend sempre responde 200 — não revela se o e-mail existe.
      await fetch(`${API_BASE}/api/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setMessage('Se o e-mail estiver cadastrado, enviamos as instruções de redefinição.');
      setError('');
    } catch (error) {
      setError('Erro ao enviar o e-mail de redefinição. Tente novamente em instantes.');
    }
  };

  const handleCompanySelect = async (company) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const idToken = await currentUser.getIdToken();
      try {
        const updateResponse = await fetch(`${API_BASE}/update-last-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ companyId: company.companyId })
        });
        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          if (updateResponse.status === 403 && errorData.error === 'Usuário bloqueado nesta empresa') {
            setError('Sua conta está bloqueada nesta empresa. Entre em contato com o administrador.');
            return;
          }
        }
      } catch (error) {
        console.error('Erro ao atualizar último login:', error);
      }
      updateSelectedCompany(company);
      setShowModal(false);
      const userRole = (company.role || company.user?.role || 'atendente').toLowerCase();
      navigate(userRole === 'admin'
        ? `/settings?companyId=${company.companyId}`
        : `/chat?companyId=${company.companyId}`);
    } catch (error) {
      updateSelectedCompany(company);
      setShowModal(false);
      const userRole = (company.role || company.user?.role || 'atendente').toLowerCase();
      navigate(userRole === 'admin'
        ? `/settings?companyId=${company.companyId}`
        : `/chat?companyId=${company.companyId}`);
    }
  };

  // ─── Render ───
  return (
    <div
      className="iat-login-outer"
      style={{
        // ⚠️ height + overflow:auto — index.css trava html/body/#root.
        height: '100vh',
        background: IAT.graphite,
        fontFamily: FONT_SANS,
        color: IAT.paper,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        overflow: 'auto',
      }}
    >
      <IatCursorGlow />
      <div
        style={{
          width: '100%',
          maxWidth: 1200,
          minHeight: 680,
          background: IAT.graphite,
          borderRadius: 18,
          overflow: 'hidden',
          border: `1px solid ${IAT.borderDark}`,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
          position: 'relative',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        }}
        className="iat-login-shell"
      >
        {/* ─── Left: Beny showcase ─── */}
        <div
          style={{
            padding: '48px 56px',
            position: 'relative',
            overflow: 'hidden',
            background: IAT.graphite,
            display: 'flex',
            flexDirection: 'column',
          }}
          className="iat-login-left"
        >
          {/* yellow glow */}
          <div style={{
            position: 'absolute', left: -100, top: 40, width: 500, height: 500,
            background: `radial-gradient(circle, rgba(255,193,7,0.20) 0%, transparent 60%)`,
            filter: 'blur(80px)', pointerEvents: 'none',
          }}/>
          {/* subtle grid */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `linear-gradient(${IAT.charcoal} 1px, transparent 1px),
                              linear-gradient(90deg, ${IAT.charcoal} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at 30% 60%, black 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 30% 60%, black 0%, transparent 70%)',
            opacity: 0.5, pointerEvents: 'none',
          }}/>

          {/* Hero copy */}
          <div style={{ position: 'relative' }}>
            <Caption style={{ color: IAT.yellow, marginBottom: 12 }}>Olá de novo</Caption>
            <h1 style={{
              fontFamily: FONT_DISPLAY, fontSize: 52, lineHeight: 0.98, letterSpacing: '-0.035em',
              margin: 0, color: IAT.paper, fontWeight: 800, maxWidth: 480,
            }}>
              A <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400, color: IAT.yellow }}>Beny</span> está esperando você.
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: IAT.pewter, marginTop: 14, maxWidth: 440 }}>
              Sua agente cuidou de tudo enquanto você estava fora. Entre e veja o que aconteceu.
            </p>
          </div>

          {/* Beny mascot */}
          <div
            className="iat-login-beny-wrap"
            style={{
              position: 'relative', marginTop: 24,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}
          >
            <div style={{ position: 'relative', width: 360, maxWidth: '100%' }}>
              <div style={{
                position: 'absolute', inset: -30,
                background: `radial-gradient(circle, rgba(255,193,7,0.28), transparent 65%)`,
                filter: 'blur(24px)',
              }}/>
              <img
                src={BenyImg}
                alt="Beny"
                style={{ position: 'relative', width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
        </div>

        {/* ─── Right: Form ─── */}
        <div
          style={{
            padding: '48px 56px',
            background: IAT.white,
            color: IAT.ink,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
          className="iat-login-right"
        >
          {isLoading && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(255,255,255,0.75)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 50,
            }}>
              <div
                style={{
                  width: 44, height: 44, borderRadius: '50%',
                  border: `3px solid ${IAT.cloud}`,
                  borderTopColor: IAT.yellowDeep,
                  animation: 'iat-spin 0.8s linear infinite',
                }}
              />
            </div>
          )}

          {/* Top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleBackToPlans}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: IAT.iron, fontSize: 13, fontFamily: FONT_SANS, padding: 0,
              }}
              aria-label="Voltar para página de planos"
            >
              <ArrowLeft size={14}/> Voltar
            </button>
            <div style={{ fontSize: 13, color: IAT.iron, display: 'flex', alignItems: 'center', gap: 10 }}>
              Novo por aqui?
              <a
                href="https://www.iatconsulting.com.br/planos"
                style={{ color: IAT.yellowDeep, textDecoration: 'none', fontWeight: 600 }}
              >
                Criar conta →
              </a>
            </div>
          </div>

          <form
            onSubmit={handleEmailLogin}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
              maxWidth: 380, margin: '0 auto', width: '100%',
            }}
          >
            <h2 style={{
              fontFamily: FONT_DISPLAY, fontSize: 40, fontWeight: 800, lineHeight: 1.05,
              letterSpacing: '-0.03em', margin: '0 0 10px', color: IAT.ink,
            }}>
              Entrar na plataforma
            </h2>
            <p style={{ fontSize: 14, color: IAT.iron, margin: '0 0 28px', lineHeight: 1.5 }}>
              Use o e-mail cadastrado na sua conta IAT.
            </p>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.08)', color: '#B91C1C',
                border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10,
                padding: '10px 12px', fontSize: 13, marginBottom: 14,
              }}>{error}</div>
            )}
            {message && (
              <div style={{
                background: 'rgba(16,185,129,0.08)', color: '#047857',
                border: '1px solid rgba(16,185,129,0.22)', borderRadius: 10,
                padding: '10px 12px', fontSize: 13, marginBottom: 14,
              }}>{message}</div>
            )}

            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <Caption style={{ marginBottom: 6 }}>Endereço de e-mail</Caption>
              <div style={{
                border: `1px solid ${IAT.cloud}`, borderRadius: 10,
                padding: '11px 14px', background: IAT.white,
              }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="você@empresa.com.br"
                  style={{
                    width: '100%', border: 'none', outline: 'none', background: 'transparent',
                    fontFamily: FONT_SANS, fontSize: 14, color: IAT.ink,
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Caption>Senha</Caption>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  style={{
                    fontSize: 11, color: IAT.yellowDeep, background: 'transparent',
                    border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0,
                    fontFamily: FONT_SANS,
                  }}
                >
                  Esqueci minha senha
                </button>
              </div>
              <div style={{
                border: `1px solid ${IAT.cloud}`, borderRadius: 10,
                padding: '11px 14px', background: IAT.white,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••"
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontFamily: FONT_SANS, fontSize: 14, color: IAT.ink,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: IAT.silver, padding: 0, display: 'flex' }}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                marginTop: 24, padding: '13px 20px',
                background: IAT.yellow, color: IAT.ink,
                border: 'none', borderRadius: 9999, fontSize: 15, fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: FONT_SANS, letterSpacing: '-0.005em',
                boxShadow: '0 4px 14px rgba(255,193,7,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                opacity: isLoading ? 0.7 : 1,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(255,193,7,0.45)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(255,193,7,0.35)';
              }}
            >
              Continuar <ArrowRight size={16} style={{ opacity: 0.7 }}/>
            </button>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
              <div style={{ flex: 1, height: 1, background: IAT.cloud }}/>
              <span style={{
                fontSize: 11, color: IAT.silver, fontWeight: 500,
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>ou</span>
              <div style={{ flex: 1, height: 1, background: IAT.cloud }}/>
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '12px 16px', background: IAT.white,
                border: `1px solid ${IAT.cloud}`, borderRadius: 9999,
                fontSize: 14, fontWeight: 500, color: IAT.ink,
                cursor: isLoading ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = IAT.paper; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = IAT.white; }}
            >
              <GoogleLogo size={16}/> Login com o Google
            </button>
          </form>

          <div style={{ textAlign: 'center', fontSize: 11, color: IAT.silver, lineHeight: 1.6 }}>
            Ao entrar você aceita os{' '}
            <a
              href="https://www.iatconsulting.com.br/termos-condicoes"
              target="_blank" rel="noopener noreferrer"
              style={{ color: IAT.iron, textDecoration: 'underline' }}
            >Termos de uso</a>{' '}e a{' '}
            <a
              href="https://www.iatconsulting.com.br/politica-privacidade"
              target="_blank" rel="noopener noreferrer"
              style={{ color: IAT.iron, textDecoration: 'underline' }}
            >Política de Privacidade</a>.
          </div>
        </div>
      </div>

      {/* spinner keyframes + responsive collapse */}
      <style>{`
        @keyframes iat-spin { to { transform: rotate(360deg); } }
        /* Modern browsers — desconta UI bar dinâmica do mobile */
        @supports (height: 100dvh) {
          .iat-login-outer { height: 100dvh !important; }
        }
        /* Custom scrollbar amarela — pista visual de scroll em desktop */
        .iat-login-outer::-webkit-scrollbar { width: 10px; }
        .iat-login-outer::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); }
        .iat-login-outer::-webkit-scrollbar-thumb {
          background: rgba(255,193,7,0.45); border-radius: 6px;
        }
        .iat-login-outer::-webkit-scrollbar-thumb:hover { background: rgba(255,193,7,0.7); }
        .iat-login-outer { scrollbar-color: rgba(255,193,7,0.45) rgba(255,255,255,0.03); scrollbar-width: thin; }

        /* Tablet/mobile (< 900px) — esconde showcase, só form */
        @media (max-width: 900px) {
          .iat-login-shell {
            grid-template-columns: 1fr !important;
            min-height: auto !important;
            border-radius: 14px !important;
            background: ${IAT.white} !important;
            border: none !important;
            box-shadow: 0 8px 30px rgba(0,0,0,0.15) !important;
            max-width: 100% !important;
          }
          .iat-login-left { display: none !important; }
          .iat-login-right { padding: 32px 28px !important; }
        }
        @media (max-width: 600px) {
          .iat-login-outer { padding: 16px !important; box-sizing: border-box; }
          .iat-login-shell {
            border-radius: 14px !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.25) !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .iat-login-right { padding: 28px 18px 80px !important; box-sizing: border-box !important; }
          .iat-login-right input,
          .iat-login-right button { max-width: 100% !important; box-sizing: border-box !important; }
        }
        @media (max-width: 380px) {
          .iat-login-outer { padding: 10px !important; }
          .iat-login-right { padding: 24px 14px 80px !important; }
        }
      `}</style>

      {showModal && (
        <CompanySelectionModal
          companies={companies}
          onSelect={handleCompanySelect}
          onClose={() => setShowModal(false)}
        />
      )}
      {showTrialExpiredModal && trialExpiredData && (
        <TrialExpiredModal
          isOpen={showTrialExpiredModal}
          onClose={() => setShowTrialExpiredModal(false)}
          userEmail={trialExpiredData.userEmail}
          subscriptionPlan={trialExpiredData.subscriptionPlan}
          type={trialExpiredData.type || 'trial'}
          isAdmin={trialExpiredData.isAdmin}
          selectedCompany={trialExpiredData.selectedCompany}
        />
      )}
    </div>
  );
};

export default LoginScreen;
